import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import {
  Box,
  Checkbox,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';

import type { SurveyDetail } from 'src/types/survey';

import { styles } from 'src/components/survey/SurveySettings/index.styles';

interface RootCauseEditProps {
  name: string;
  isActive: boolean;
  index: number;
}

const RootCauseEdit: FC<RootCauseEditProps> = ({ name, isActive, index }) => {
  const { t } = useTranslation('survey');
  const [disabled, setDisabled] = useState(true);
  const { handleChange, errors } = useFormikContext<SurveyDetail>();
  const error = errors?.specificSettings?.followUp?.rootCauses?.[index];
  const textFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus input when activating editing
    if (!disabled) textFieldRef.current?.focus();
  }, [disabled]);

  return (
    <Box
      sx={styles.rootCauseEdit}
      aria-label={t('settings.followUpModule.rootCauseEdit')}
    >
      <Checkbox
        checked={isActive}
        onChange={handleChange}
        name={`specificSettings.followUp.rootCauses[${index}].isActive`}
        color="primary"
        inputProps={{ 'aria-label': t('settings.followUpModule.isActive') }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <TextField
          inputRef={textFieldRef}
          disabled={disabled}
          value={name}
          name={`specificSettings.followUp.rootCauses[${index}].name`}
          onChange={handleChange}
          inputProps={{ 'aria-label': t('specificSettings.followUp.name') }}
        />
      </Box>
      <IconButton
        aria-label={
          disabled
            ? t('settings.followUpModule.editRootCause')
            : t('settings.followUpModule.stopEditingRootCause')
        }
        onClick={() => {
          setDisabled((prev) => !prev);
        }}
        sx={(theme) => ({
          backgroundColor: theme.colors.primary[50],
          borderRadius: '4px',
        })}
      >
        {disabled ? (
          <EditOutlined color="primary" />
        ) : (
          <CheckCircleOutline color="primary" />
        )}
      </IconButton>
      {error && (
        <Typography
          variant="body4"
          color="error"
          sx={{
            gridColumn: '2 / 3',
          }}
        >
          {typeof error === 'string' ? error : error.name}
        </Typography>
      )}
    </Box>
  );
};

export default RootCauseEdit;
